import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { HydrationBoundary, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App, ConfigProvider } from 'antd';
import MainLayout from 'src/components/Layout/MainLayout';
import 'antd/dist/reset.css';
import 'src/styles/index.less';
type NextPageWithLayout = NextPage & {
  getLayout?: ComponentType<any>;
};
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};
const MyApp = (props: AppPropsWithLayout) => {
  const {
    Component,
    pageProps
  } = props;
  const router = useRouter();
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000
      }
    }
  }));
  const Layout = Component.getLayout ?? MainLayout;
  const combinedProps = {
    ...pageProps
  };
  useEffect(() => {
    const handleRouteChange = () => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'gtm.js',
        'gtm.start': new Date().getTime()
      });
      window.dataLayer.push({
        event: 'gtm.dom'
      });
      window.dataLayer.push({
        event: 'gtm.load'
      });
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => router.events.off('routeChangeComplete', handleRouteChange);
  }, [router.events]);
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver') && args[0].includes('children')) {
        return;
      }
      originalError(...args);
    };
    const createInsiderProxy = (obj?: any): any => {
      const store = obj || (() => {});
      if (!obj) {
        store.isDummy = true;
      }
      return new Proxy(store, {
        get: (target: any, prop: any) => {
          if (prop === 'then') return undefined;
          if (Object.prototype.hasOwnProperty.call(target, prop) || prop in target) {
            const val = target[prop];
            if (val !== undefined) return val;
          }
          const newProxy = createInsiderProxy();
          target[prop] = newProxy;
          return newProxy;
        },
        set: (target: any, prop: any, value: any) => {
          target[prop] = value;
          return true;
        },
        apply: (target: any, thisArg: any, argumentsList: any) => {
          if (target.isDummy) {
            return createInsiderProxy();
          }
          return Reflect.apply(target, thisArg, argumentsList);
        },
        construct: (target: any, argumentsList: any, newTarget: any) => {
          if (target.isDummy) {
            return createInsiderProxy();
          }
          return Reflect.construct(target, argumentsList, newTarget);
        }
      });
    };
    const dummyTarget = () => {};
    (dummyTarget as any).isDummy = true;
    (window as any).Insider = (window as any).Insider || createInsiderProxy(dummyTarget);
  }, []);
  return (
    // <StyleProvider ssrInline>
    <ConfigProvider theme={{
      token: {
        colorPrimary: '#F48A8A',
        colorPrimaryHover: '#f07070',
        colorPrimaryActive: '#e85e5e',
        fontFamily: '"kalbesystem", sans-serif',
        colorText: '#4A4545',
        colorTextBase: '#4A4545',
        colorTextSecondary: '#4A4545',
        colorTextTertiary: '#4A4545',
        colorTextQuaternary: '#4A4545'
      },
      components: {
        Button: {
          colorTextDisabled: '#9ca3aa'
        },
        Input: {
          colorTextPlaceholder: '#9ca3aa'
        },
        Select: {
          colorTextPlaceholder: '#9ca3aa'
        }
      }
    }}>
			<QueryClientProvider client={queryClient}>
				<HydrationBoundary state={pageProps.dehydratedState}>
					<App>
						<Layout {...combinedProps} router={router}>
							<Component {...combinedProps} router={router} />
						</Layout>
					</App>
				</HydrationBoundary>
			</QueryClientProvider>
		</ConfigProvider>
    // </StyleProvider>
  );
};
export default MyApp;